import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { EiaClient, ApiFailure } from '../api/client'
import type { EngagementSummary, ListFilter } from '../api/types'
import { useAuth } from '../auth/useAuth'
import { ListStream } from '../stream/list'
import { Topbar } from '../components/chrome/Topbar'
import { PHASES } from '../phases'
import { age } from '../components/engagement/Rail'

const FILTERS: readonly { value: ListFilter | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'needs-you', label: 'Needs you' },
  { value: 'working', label: 'Working' },
  { value: 'signed', label: 'Signed' },
  { value: 'closed', label: 'Closed' },
]

/**
 * The engagements list. `04 §4.2` — SIGNING IN LANDS HERE, NOT ON A BLANK PROMPT.
 *
 * <p>A blank prompt asks a person to invent a question before they have seen what the system does
 * with one. The list shows them their own work first, and the prompt is one control on it.</p>
 */
export function Workspace(): React.JSX.Element {
  const { token, stale } = useAuth()
  const client = useMemo(() => new EiaClient(token), [token])

  const [items, setItems] = useState<EngagementSummary[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [filter, setFilter] = useState<ListFilter | 'all'>('all')
  const [error, setError] = useState<ApiFailure | null>(null)
  const [busy, setBusy] = useState(false)
  const [prompt, setPrompt] = useState('')

  /**
   * How many have MOVED since the list was rendered.
   *
   * <p>The list is NOT re-sorted underneath the reader. Somebody comparing two rows should not have
   * them swap while they look, so the change is offered as a control and applied when they ask.</p>
   */
  const [moved, setMoved] = useState(0)

  /** Whether the list stream is connected, for the indicator. `04 §4.4`. */
  const [live, setLive] = useState(false)

  /**
   * HOW MANY ARE IN EACH FILTER, which `Frontend/workspace.html` puts on every tab.
   *
   * <p>"Needs you is reachable in one action from landing. Everything else on this page is a
   * convenience; this one is the product." A tab that says <b>2</b> tells a reader whether to
   * press it; a tab that says nothing makes them press all five to find out.</p>
   */
  const [counts, setCounts] = useState<Record<string, number> | null>(null)

  const load = useCallback(async (reset: boolean) => {
    setBusy(true)
    try {
      const page = await client.list({
        limit: 20,
        ...(reset ? {} : cursor ? { cursor } : {}),
        ...(filter === 'all' ? {} : { filter }),
      })
      setItems((current) => reset ? page.items : [...current, ...page.items])
      setCursor(page.nextCursor)
      setError(null)
      if (reset) setMoved(0)
    } catch (failure) {
      if (failure instanceof ApiFailure) setError(failure)
    } finally {
      setBusy(false)
    }
  }, [client, cursor, filter])

  useEffect(() => { void load(true) }, [filter])   // eslint-disable-line react-hooks/exhaustive-deps

  // RE-COUNTED WHEN SOMETHING MOVES, not on a timer. `moved` is incremented by the list stream for
  // exactly the transitions that can change a count.
  useEffect(() => {
    void client.counts().then(setCounts).catch(() => {
      // A tab without its number is a tab; a page that refused to render because a count was
      // unavailable would be worse than one that shows the list.
    })
  }, [client, moved, items.length])

  // THE VIEW IS STAMPED ON THE BODY, because the lifted stylesheets scope their layout to it.
  useEffect(() => {
    document.body.dataset['view'] = 'workspace'
    return () => { delete document.body.dataset['view'] }
  }, [])

  /**
   * ONE STREAM FOR THE WHOLE LIST (M-5), and it does NOT re-sort under the reader.
   *
   * <p>A row that moved is counted and the count becomes a control. Somebody comparing two rows
   * should not have them swap while they look at them — the list re-sorts when they ask it to, and
   * the count is how they know there is something to ask for.</p>
   */
  useEffect(() => {
    const stream = new ListStream(token, (frame) => {
      const id = typeof frame['engagementId'] === 'string' ? frame['engagementId'] : null
      if (id === null) return

      setItems((current) => {
        const index = current.findIndex((row) => row.engagementId === id)

        // AN ENGAGEMENT NOT ON THIS PAGE IS STILL NEWS. It may be one the filter excludes, or one
        // on a page not loaded — either way the reader is told something moved rather than shown
        // a row appearing from nowhere in the middle of their list.
        if (index < 0) {
          setMoved((n) => n + 1)
          return current
        }

        const row = current[index]
        if (!row) return current

        const lifecycle = typeof frame['lifecycle'] === 'string' ? frame['lifecycle'] : row.lifecycle
        const activity = typeof frame['activity'] === 'string' ? frame['activity'] : row.activity
        if (lifecycle === row.lifecycle && activity === row.activity) return current

        setMoved((n) => n + 1)
        const next = [...current]
        next[index] = { ...row, lifecycle, activity } as typeof row
        return next
      })
    }, setLive)

    void stream.start()
    return () => stream.stop()
  }, [token])

  const start = async () => {
    const question = prompt.trim()
    if (question.length === 0) return

    setBusy(true)
    try {
      // M-16: the same key returns the same engagement, so a retried POST — a double-click, a flaky
      // connection — does not start two.
      const started = await client.start(question, crypto.randomUUID())
      setPrompt('')
      window.location.assign(`/e/${encodeURIComponent(started.engagementId)}`)
    } catch (failure) {
      if (failure instanceof ApiFailure) setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Topbar connection={live ? 'open' : 'connecting'} stale={stale} />

      <main className="page" id="main">
        <h1 className="display" style={{ fontSize: '22px', margin: '0 0 18px' }}>Engagements</h1>

        {/* §3.4: ONE TEXT BOX AND ONE BUTTON. Nothing else to fill in — scope, method and every
            parameter are settled with the user as the work proceeds, which is the product. */}
        <form className="ask" onSubmit={(e) => { e.preventDefault(); void start() }}>
          <label className="skip" htmlFor="q">Ask a new question</label>
          <input id="q" type="text" value={prompt} onChange={(e) => setPrompt(e.target.value)}
                 placeholder="Ask a new economic-impact question…" />
          <button className="btn primary" type="submit"
                  disabled={busy || prompt.trim().length === 0}>Start</button>
        </form>
        <p className="askhint">
          One question, in your own words. Nothing else to fill in: scope, method and every
          parameter are settled with you as the work proceeds.
        </p>

        {error && <ErrorNotice failure={error} />}

        {/* §4.5: "Needs you is reachable in one action from landing. Everything else on this page
            is a convenience; this one is the product." */}
        <div className="filters" role="tablist" aria-label="Filter engagements">
          {FILTERS.map((f) => (
            <button key={f.value} role="tab" type="button" aria-selected={filter === f.value}
                    className={f.value === 'needs-you' ? 'needs' : undefined}
                    onClick={() => setFilter(f.value)}>
              {f.label}
              {counts !== null && counts[f.value] !== undefined && (
                <span className="n">{counts[f.value]}</span>
              )}
            </button>
          ))}
        </div>

        {moved > 0 && (
          <div className="more">
            <button type="button" className="btn quiet" onClick={() => void load(true)}>
              ↻ {moved} engagement{moved === 1 ? ' has' : 's have'} moved
            </button>
          </div>
        )}

        {items.length > 0 && (
          <div>
            <div className="lhead">
              <p />
              <p className="mlabel">Ask</p>
              <p className="mlabel">Phase</p>
              <p className="mlabel">Status</p>
              <p className="mlabel" style={{ textAlign: 'right' }}>Updated</p>
            </div>

            {items.map((item) => <Row key={item.engagementId} item={item} />)}

            {/* CURSOR PAGING. No numbered pages and no total: an unbounded collection with a count
                is a count somebody pays to compute on every request. */}
            {cursor && (
              <div className="more">
                <button type="button" className="btn quiet" disabled={busy}
                        onClick={() => void load(false)}>Load more</button>
              </div>
            )}
          </div>
        )}

        {/* §4.7: NOT AN EMPTY TABLE. */}
        {items.length === 0 && !busy && filter === 'all' && (
          <div className="empty">
            <h2>Nothing here yet: ask your first question</h2>
            <p>
              Ask an economic-impact question in your own words. The advisor works out what kind of
              analysis it needs, tells you what it cannot tell you before it tells you anything
              else, and shows where every number came from.
            </p>
            <div className="example">
              <p className="mlabel">A worked example</p>
              <q>{EXAMPLE}</q>
            </div>
            <button type="button" className="btn primary"
                    onClick={() => { setPrompt(EXAMPLE); }}>
              Use this example <span className="arrow" aria-hidden="true">→</span>
            </button>
          </div>
        )}

        {/* §4.7's second MUST: a filter that matched nothing is not the same as having nothing. */}
        {items.length === 0 && !busy && filter !== 'all' && (
          <div className="noresults">
            <p>No engagements are <b>{FILTERS.find((f) => f.value === filter)?.label}</b> right now.</p>
            <button type="button" className="btn quiet" onClick={() => setFilter('all')}>
              Clear filter
            </button>
          </div>
        )}
      </main>
    </>
  )
}

/** The worked example `§4.7` offers on a first run. The specification's own wording. */
const EXAMPLE = 'There is an investment of a 100m AED in a compute facility that will be in '
  + 'Abu Dhabi; what would be the GDP impact and how many jobs are expected to be created?'

/**
 * One row. `Frontend/workspace.html`.
 *
 * <p><b>THE GATE KIND AND ITS AGE, NEVER A GENERIC "awaiting input"</b> (`§3.3.3`): "a gate that
 * has been open for six days is the single most actionable fact the list holds". Both come off the
 * engagement document; neither is guessed, and a row whose server did not say renders neither.</p>
 *
 * <p><b>A HALT IS A PRODUCT OUTCOME AND CARRIES ITS ROUTE FORWARD</b> (`§4.4`, `§3.3.1`) — "a
 * refusal without a route forward is not useful". A FAULT is never rendered as one: "rendering them
 * identically would tell a user that the system considered their question and declined, when in
 * fact it fell over."</p>
 */
function Row({ item }: { item: EngagementSummary }): React.JSX.Element {
  const phase = PHASES.find((p) => p.number === item.phase)
  const awaiting = item.activity === 'awaiting-input'

  return (
    <Link className="row" to={`/e/${encodeURIComponent(item.engagementId)}`} title={item.askSummary}>
      <span className={`glyph g ${glyph(item)}`} aria-hidden="true">{glyphText(glyph(item))}</span>

      <span className="ask-cell">
        <span className="t">{item.askSummary}</span>
        <span className="id">{item.engagementId}</span>
      </span>

      <span className="phase">
        {phase !== undefined && (
          <span className="p"><b>{phase.number}</b>{phase.name}</span>
        )}
      </span>

      <span className="st">
        <span className={`status${awaiting ? ' awaiting' : ''}`}>
          <span className="lead">{lead(item)}</span>
        </span>
        <span className="spec">{detail(item)}</span>
      </span>

      <span className="upd" title={item.lastActivityAt ?? item.startedAt}>
        {age(item.lastActivityAt ?? item.startedAt)}
      </span>
    </Link>
  )
}

function lead(item: EngagementSummary): string {
  if (item.activity === 'awaiting-input') return 'Awaiting you'
  if (item.lifecycle === 'signed') return 'Signed'
  if (item.lifecycle === 'halted') return 'Stopped'
  if (item.lifecycle === 'cancelled') return 'Cancelled'

  // A FAULT IS NEVER PRESENTED AS AN ANALYTICAL OUTCOME (`§3.3.1`): "rendering them identically
  // would tell a user that the system considered their question and declined, when in fact it
  // fell over."
  if (item.lifecycle === 'faulted') return 'Interrupted'
  if (item.activity === 'throttled') return 'Waiting on model capacity'
  return 'Working'
}

/**
 * The second line. IT SAYS WHAT THE FIRST DOES NOT, and nothing when there is nothing to say —
 * a row that repeats its own status twice has spent a line to tell the reader nothing.
 */
function detail(item: EngagementSummary): string {
  if (item.activity === 'awaiting-input' && item.openGateKind !== undefined
      && item.openGateKind !== null) {
    const kind = item.openGateKind.replace(/_/g, ' ')
    return item.openGateOpenedAt ? `${kind} · open ${age(item.openGateOpenedAt)}` : kind
  }
  return ''
}

function glyph(item: EngagementSummary): string {
  if (item.activity === 'awaiting-input') return 'g-await'
  if (item.lifecycle === 'signed') return 'g-done'
  if (item.lifecycle === 'halted' || item.lifecycle === 'cancelled') return 'g-halt'
  if (item.lifecycle === 'faulted') return 'g-fault'
  if (item.activity === 'throttled') return 'g-pending'
  return 'g-current'
}

function glyphText(g: string): string {
  switch (g) {
    case 'g-await': return '⛔︎'
    case 'g-done': return '✓'
    case 'g-halt': return '✗︎'
    case 'g-fault': return '⚠︎'
    case 'g-pending': return '⌛︎'
    default: return '●'
  }
}

/** A refusal, with its correlation id so a report of it can be traced. Never a stack. */
export function ErrorNotice({ failure }: { failure: ApiFailure }): React.JSX.Element {
  return (
    <div className="notice notice--error" role="alert">
      <p>{failure.message}</p>
      {failure.correlationId && (
        <p className="notice__correlation">
          If you report this, quote <code>{failure.correlationId}</code>.
        </p>
      )}
    </div>
  )
}
